import React, { useState } from "react";
import {
  BarChart,
  BookOpen,
  Box,
  PieChart,
  UserCheck,
  Users,
} from "react-feather";
import styled from "styled-components";
import HeroCharts from "./HeroCharts";
import ClickableItem from "./ClickableItem";

const OrgTitle = () => (
  <div
    style={{
      display: "flex",
      gap: 4,
      alignItems: "center",
      justifyContent: "flex-start",
    }}
  >
    <Box height={13} width={13} color="slategrey" />
    <span
      style={{
        color: "slategrey",
        fontSize: 12,
        fontWeight: 500,
        marginTop: 1,
      }}
    >
      ORGANIZATION
    </span>
  </div>
);

const ClickableItemContainer = styled.div`
  width: 100%;
  height: 130px;
  overflow-y: scroll;
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
  padding-bottom: 16px;
  gap: 12px;
  padding-right: 20px;

  &::-webkit-scrollbar {
    height: 8px;
  }

  scrollbar-width: auto;
  scrollbar-color: #ccc transparent;
`;

const ChartsWrapper = styled.div`
  min-height: 200px;
  background-color: white;
  width: 100%;
  border-radius: 12px;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
`;

const Spacer = styled.div`
  height: 1px;
  width: 100%;
  background-color: lightgray;
  margin-bottom: 16px;
`;

const retPercentageOfTotal = (total, active) => {
  if (!total || !active) return 0;

  return Math.floor((active / total) * 100);
};

const retColor = (index) => {
  if (index === 0) return "#337ab7";
  if (index === 1) return "#3379b7d9";
  if (index === 2) return "#3379b7b1";
  if (index === 3) return "#3379b797";
  if (index === 4) return "#3379b764";
  return "#337ab790";
};

const FILTER_OPTIONS = {
  ALL_TIME_SUBS: "All Time Submissions",
  ALL_TIME_COMPS: "All Time Completions",
  TOTAL_TEAM_MEM: "Total Team Members",
  LIVE_MODULES: "Live Modules",
  ACITVE_TEAM_MEMBERS: "Active Team Members",
  RECENT_SUBS: "Recent Submissions",
  RECENT_COMPS: "Recent Completions",
};

const returnPercentageValue = (activeFilter, company, totals) => {
  if (activeFilter === FILTER_OPTIONS.ALL_TIME_SUBS) {
    return retPercentageOfTotal(
      totals.totalSubmissions,
      company?.data.submissions.submissions,
    );
  }
  if (activeFilter === FILTER_OPTIONS.ALL_TIME_COMPS) {
    return retPercentageOfTotal(
      totals.totalCompletions,
      company?.data?.submissions?.completions,
    );
  }
  if (activeFilter === FILTER_OPTIONS.TOTAL_TEAM_MEM) {
    return retPercentageOfTotal(
      totals.totalTeamMembers,
      company?.data?.team_members.overall,
    );
  }
  if (activeFilter === FILTER_OPTIONS.LIVE_MODULES) {
    return retPercentageOfTotal(
      totals.totalLiveModules,
      company?.data?.live_modules,
    );
  }
  if (activeFilter === FILTER_OPTIONS.ACITVE_TEAM_MEMBERS) {
    return retPercentageOfTotal(
      totals.totalRecentlyActiveTeamMembers,
      company.data.team_members.recently_active,
    );
  }
  if (activeFilter === FILTER_OPTIONS.RECENT_SUBS) {
    return retPercentageOfTotal(
      totals.totalRecentSubmissions,
      company?.data?.submissions.recent_submissions,
    );
  }
  if (activeFilter === FILTER_OPTIONS.RECENT_COMPS) {
    return retPercentageOfTotal(
      totals.totalRecentCompletions,
      company?.data?.submissions.recent_completions,
    );
  }
};

const returnValue = (activeFilter, company) => {
  if (activeFilter === FILTER_OPTIONS.ALL_TIME_SUBS)
    return company?.data?.submissions?.submissions;
  if (activeFilter === FILTER_OPTIONS.ALL_TIME_COMPS)
    return company?.data?.submissions?.completions;
  if (activeFilter === FILTER_OPTIONS.TOTAL_TEAM_MEM)
    return company?.data.team_members.overall;
  if (activeFilter === FILTER_OPTIONS.LIVE_MODULES)
    return company?.data.live_modules;
  if (activeFilter === FILTER_OPTIONS.ACITVE_TEAM_MEMBERS)
    return company?.data?.team_members?.recently_active;
  if (activeFilter === FILTER_OPTIONS.RECENT_SUBS)
    return company?.data?.submissions?.recent_submissions;
  if (activeFilter === FILTER_OPTIONS.RECENT_COMPS)
    return company?.data?.submissions?.recent_completions;
};

const Hero = ({ name, companyArray, totals }) => {
  const [activeFilter, setActiveFilter] = useState(
    FILTER_OPTIONS.ALL_TIME_SUBS,
  );

  const statsForActiveFilter = companyArray
    .map((company) => ({
      name: company?.company_name,
      value: returnValue(activeFilter, company),
      percentageOfTotalValue: returnPercentageValue(
        activeFilter,
        company,
        totals,
      ),
    }))
    .sort((a, b) => b.value - a.value)
    .map((company, i) => ({
      ...company,
      color: retColor(i),
    }));

  return (
    <>
      <OrgTitle />
      <p style={{ fontSize: 20, fontWeight: 600, marginTop: -2 }}>{name}</p>
      <div style={{ minHeight: 12 }} />
      <ClickableItemContainer>
        <ClickableItem
          title={FILTER_OPTIONS.ALL_TIME_SUBS}
          value={totals.totalSubmissions}
          Icon={BarChart}
          isActive={activeFilter === FILTER_OPTIONS.ALL_TIME_SUBS}
          setActiveFilter={setActiveFilter}
        />
        <ClickableItem
          title={FILTER_OPTIONS.ALL_TIME_COMPS}
          value={totals.totalCompletions}
          Icon={PieChart}
          isActive={activeFilter === FILTER_OPTIONS.ALL_TIME_COMPS}
          setActiveFilter={setActiveFilter}
        />
        <ClickableItem
          title={FILTER_OPTIONS.TOTAL_TEAM_MEM}
          value={totals.totalTeamMembers}
          Icon={Users}
          isActive={activeFilter === FILTER_OPTIONS.TOTAL_TEAM_MEM}
          setActiveFilter={setActiveFilter}
        />
        <ClickableItem
          title={FILTER_OPTIONS.LIVE_MODULES}
          value={totals.totalLiveModules}
          Icon={BookOpen}
          isActive={activeFilter === FILTER_OPTIONS.LIVE_MODULES}
          setActiveFilter={setActiveFilter}
        />
        <ClickableItem
          title={FILTER_OPTIONS.ACITVE_TEAM_MEMBERS}
          Icon={UserCheck}
          value={totals.totalRecentlyActiveTeamMembers}
          isActive={activeFilter === FILTER_OPTIONS.ACITVE_TEAM_MEMBERS}
          setActiveFilter={setActiveFilter}
          explanationText="Team members with at least 1 submission last 14 days"
        />
        <ClickableItem
          title={FILTER_OPTIONS.RECENT_SUBS}
          Icon={BarChart}
          value={totals.totalRecentSubmissions}
          isActive={activeFilter === FILTER_OPTIONS.RECENT_SUBS}
          setActiveFilter={setActiveFilter}
          explanationText="Submissions last 14 days"
        />
        <ClickableItem
          title={FILTER_OPTIONS.RECENT_COMPS}
          Icon={PieChart}
          value={totals.totalRecentCompletions}
          isActive={activeFilter === FILTER_OPTIONS.RECENT_COMPS}
          setActiveFilter={setActiveFilter}
          explanationText="Completions last 14 days"
        />
      </ClickableItemContainer>
      <Spacer />
      <ChartsWrapper>
        <HeroCharts
          activeFilter={activeFilter}
          statsForActiveFilter={statsForActiveFilter}
        />
      </ChartsWrapper>
    </>
  );
};

export default Hero;
